

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { Typography } from '@mui/material';
import {
    
    CollaboratingGroupContext,
    DatePicker,
    FileUpload,
    FormOptionsContext,
    GroupedFields,
    GroupedTableArrayField,
    IFileUpload,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PatientContext,
    Select
} from '@ngt/opms';
import { FC, useMemo } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../api/dtos';
import React from 'react';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { useFormActions } from '@ngt/forms-core';


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * constant
 * ---------------------------------------------------------------------------------
 */
const existingLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/view/${file?.id}`;
}

const existingDownloadLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/download/${file?.id}`;
}

const ImageReportDocuments: FC = () => {
    const { data: masterGroup } = React.useContext(MasterGroupContext);

    const { data: collaboratingGroup } = React.useContext(CollaboratingGroupContext);

    const { data: institution } = React.useContext(InstitutionContext);

    const { data: patient } = React.useContext(PatientContext);

    const { permissions: [ canAdministerOpms ] } = usePermissionsByIds([Permission.OpmsAdminister], masterGroup?.id, collaboratingGroup?.id, institution?.id, patient?.id);

    const { setFieldValue, submit } = useFormActions();

    const { readOnly } = React.useContext(FormOptionsContext);

    const onFileUploadChange = React.useCallback((value?: IFileUpload) => {
        if(readOnly && value?.upload != null) {
            setFieldValue('submitType', 'save');

            submit();
        }
    }, [setFieldValue, submit, readOnly]);

    const columns: Array<IGroupedTableArrayFieldColumn<Dtos.ImageReportDocument>> = useMemo(() => {
        return (
            [
                {
                    propertyPath: 'fileTypeId',
                    content: (
                        <Input component={Select} />
                    )
                },
                {
                    propertyPath: 'fileDate',
                    content: (
                        <Input 
                            disableFuture
                            component={DatePicker} 
                            format="dd/MM/yyyy"
                        />
                    )
                },
                {
                    propertyPath: 'file',
                    content: (
                        <Input 
                            component={FileUpload} 
                            existingLink={existingLink}
                            existingDownloadLink={existingDownloadLink}
                            redactable
                            allowRedactOnFormDisable={canAdministerOpms ?? false}
                            onChange={onFileUploadChange}
                        /> 
                    )
                }
            ]
        );
    }, [canAdministerOpms]);

    return (
        <>
            <GroupedFields>
                <Typography
                    variant="h2"
                    sx={{ px: 2, pt: 3, pb: 1}}
                >
                    Image Report Documents
                </Typography>
                <Typography sx={{px: 2, py: 3}}>
                    Please upload remaining imaging reports here (at least one mammogram report required).
                </Typography>
                <GroupedTableArrayField
                    propertyPath="imageReportDocuments"
                    entityName="Document"
                    columns={columns}
                    AddButtonProps={{
                        sx: {
                            mb: 2
                        }
                    }}
                />
            </GroupedFields>
        </>
    );
}

export default ImageReportDocuments